import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { typography, borders, textColors, surfaces, interactive } from "../../../design-system/tokens";
import { ChevronDownIcon, ChevronUpIcon } from "../../../design-system/icons";
import { getFontFamilyPreviewStack } from "../../../core/utils/fontFamilyUtils";
import { useExpandedField } from "../../context/ExpandedFieldContext";
import {
  addRecentFontFamily,
  getRecentFontFamilies,
  RECENT_FONT_FAMILIES_UPDATED_EVENT,
} from "../../utils/recentFonts";

const COLLAPSED_GRID_MAX_HEIGHT = 240;
const EXPANDED_VIEWPORT_MARGIN = 24;
const RECENT_FONTS_LABEL = "Recently Used";

function FontChip({ value, label, previewText, selected, onSelect }) {
  const stack = getFontFamilyPreviewStack(value);
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      className={`w-full text-left border rounded-lg px-3 py-2 mb-2 transition-all ${selected ? `${interactive.selectedBg} text-white ${interactive.selectedBorder} shadow-sm` : `${borders.default} ${surfaces.primary} ${surfaces.hover}`}`}
      aria-pressed={selected}
    >
      <div style={{ fontFamily: stack }} className={`font-medium ${typography.body} ${selected ? "text-white" : textColors.primary}`}>{label}</div>
      <div
        style={{ fontFamily: stack }}
        className={`${selected ? "text-white/70" : textColors.secondary} mt-1 whitespace-nowrap overflow-hidden text-ellipsis ${typography.caption}`}
        title={typeof previewText === 'string' ? previewText : ''}
      >
        {previewText}
      </div>
    </button>
  );
}

function getPreviewTextFromValues(allValues) {
  if (!allValues || typeof allValues !== 'object') return null;
  const candidateFields = [
    "text",
    "title",
    "label",
    "button_text",
    "subtitle",
    "content",
    "description",
    "name",
  ];
  for (const key of candidateFields) {
    const v = allValues[key];
    if (typeof v === "string" && v.trim().length > 0) {
      return v.trim();
    }
  }
  return null;
}

function getTextFromSelectedElement(selectedElement) {
  if (!selectedElement || typeof selectedElement !== 'object') return null;
  const perType = {
    title: ["text", "title"],
    text: ["text", "content"],
    button: ["text", "label", "button_text"],
    hero: ["title", "subtitle", "text"],
    image: ["caption", "alt", "title"],
  };
  const keys = perType[selectedElement.type] || ["text", "title", "label", "name", "description"];
  for (const key of keys) {
    const v = selectedElement[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return null;
}

export default function FontFamilyField({ label, help, value, onChange, choices, allValues, selectedElement }) {
  // Use shared expanded field context for accordion behavior
  const fieldId = "font-family-field";
  let isExpandedContext;
  let setExpandedContext;
  try {
    const context = useExpandedField();
    isExpandedContext = context.isExpanded;
    setExpandedContext = context.setExpanded;
  } catch {
    isExpandedContext = null;
    setExpandedContext = null;
  }
  const [localIsExpanded, setLocalIsExpanded] = useState(false);
  const isExpanded = isExpandedContext ? isExpandedContext(fieldId) : localIsExpanded;
  const [expandedHeight, setExpandedHeight] = useState(COLLAPSED_GRID_MAX_HEIGHT);
  const [recentFontValues, setRecentFontValues] = useState(() => getRecentFontFamilies());
  const [searchQuery, setSearchQuery] = useState("");
  const gridRef = useRef(null);
  const helpRef = useRef(null);
  const prevSelectedElementIdentityRef = useRef(null);
  const selectedElementIdentity = [
    selectedElement?.type || "unknown",
    selectedElement?.id ?? selectedElement?.subdomain ?? selectedElement?.name ?? "none",
  ].join(":");
  const fallback = "The quick brown fox jumps over the lazy dog";
  const textRaw = getTextFromSelectedElement(selectedElement) || getPreviewTextFromValues(allValues) || fallback;
  const previewText = textRaw.length > 160 ? textRaw.slice(0, 157) + "…" : textRaw;

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleRecentFontsUpdated = (event) => {
      setRecentFontValues(Array.isArray(event?.detail) ? event.detail : []);
    };

    setRecentFontValues(getRecentFontFamilies());
    window.addEventListener(RECENT_FONT_FAMILIES_UPDATED_EVENT, handleRecentFontsUpdated);

    return () => {
      window.removeEventListener(RECENT_FONT_FAMILIES_UPDATED_EVENT, handleRecentFontsUpdated);
    };
  }, []);

  useEffect(() => {
    if (!isExpanded || typeof window === "undefined") return undefined;

    const updateExpandedHeight = () => {
      const gridElement = gridRef.current;
      if (!gridElement) return;

      const gridRect = gridElement.getBoundingClientRect();
      const helpHeight = helpRef.current?.offsetHeight || 0;
      const availableHeight = window.innerHeight - gridRect.top - helpHeight - EXPANDED_VIEWPORT_MARGIN;
      setExpandedHeight(Math.max(COLLAPSED_GRID_MAX_HEIGHT, Math.floor(availableHeight)));
    };

    const rafId = window.requestAnimationFrame(updateExpandedHeight);
    window.addEventListener("resize", updateExpandedHeight);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", updateExpandedHeight);
    };
  }, [isExpanded, help]);

  useEffect(() => {
    // Only collapse when the selected editor item actually changes, not on mount
    // or when expanded state changes (which would cause isExpandedContext identity to shift).
    if (prevSelectedElementIdentityRef.current === selectedElementIdentity) {
      prevSelectedElementIdentityRef.current = selectedElementIdentity;
      return;
    }
    prevSelectedElementIdentityRef.current = selectedElementIdentity;

    setExpandedHeight(COLLAPSED_GRID_MAX_HEIGHT);
    setSearchQuery("");

    if (setExpandedContext) {
      setExpandedContext(null);
      return;
    }

    setLocalIsExpanded(false);
  }, [selectedElementIdentity, setExpandedContext]);

  useEffect(() => {
    if (!isExpanded) {
      setSearchQuery("");
    }
  }, [isExpanded]);

  const gridStyle = {
    gridTemplateColumns: isExpanded ? "1fr" : "repeat(auto-fit, minmax(min(200px, 100%), 1fr))",
    maxHeight: isExpanded ? expandedHeight : COLLAPSED_GRID_MAX_HEIGHT,
    overflowY: "auto",
    alignContent: "start",
  };

  if (isExpanded) {
    gridStyle.height = expandedHeight;
  }

  const choiceMap = useMemo(
    () => new Map((choices || []).map((choice) => [choice.value, choice])),
    [choices],
  );

  const recentChoices = useMemo(
    () => recentFontValues.map((fontValue) => choiceMap.get(fontValue)).filter(Boolean),
    [choiceMap, recentFontValues],
  );

  const normalizedSearchQuery = useMemo(
    () => searchQuery.trim().toLowerCase(),
    [searchQuery],
  );

  const matchesSearch = useCallback((choice) => {
    if (!normalizedSearchQuery) return true;

    const labelText = typeof choice?.label === "string"
      ? choice.label
      : typeof choice?.value === "string"
        ? choice.value
        : "";

    return labelText.toLowerCase().includes(normalizedSearchQuery)
      || String(choice?.value || "").toLowerCase().includes(normalizedSearchQuery);
  }, [normalizedSearchQuery]);

  const filteredRecentChoices = useMemo(
    () => recentChoices.filter(matchesSearch),
    [normalizedSearchQuery, recentChoices],
  );

  const filteredAllChoices = useMemo(
    () => (choices || []).filter(matchesSearch),
    [normalizedSearchQuery, choices],
  );

  const hasSearchResults = filteredRecentChoices.length > 0 || filteredAllChoices.length > 0;

  const handleSelect = (selectedFontFamily) => {
    const updatedRecentFonts = addRecentFontFamily(selectedFontFamily);
    if (updatedRecentFonts) {
      setRecentFontValues(updatedRecentFonts);
    }
    onChange(selectedFontFamily);
  };

  const handleToggleExpanded = () => {
    if (setExpandedContext) {
      setExpandedContext(fieldId);
      return;
    }

    setLocalIsExpanded((current) => !current);
  };

  return (
    <div className="field-wrapper">
      <div className={`mb-2 flex items-center gap-2 ${isExpanded ? "" : "flex-wrap justify-between"}`}>
        <label className={`${typography.label} ${textColors.secondary}`}>{label}</label>
        {isExpanded && (
          <div
            data-testid="font-family-title-divider"
            className={`min-w-0 flex-1 border-t ${borders.default}`}
          />
        )}
        <button
          type="button"
          onClick={handleToggleExpanded}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? "Collapse font family options" : "Expand font family options"}
          className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 ${borders.default} ${surfaces.primary} ${surfaces.hover} ${textColors.secondary} ${typography.caption}`}
        >
          {isExpanded ? <ChevronUpIcon size="sm" /> : <ChevronDownIcon size="sm" />}
          <span>{isExpanded ? "Collapse" : "Expand"}</span>
        </button>
      </div>
      <div
        ref={gridRef}
        data-testid="font-family-grid"
        className="grid gap-2"
        style={gridStyle}
      >
        {isExpanded && (
          <div style={{ gridColumn: "1 / -1" }} className="sticky top-0 z-10 pb-1">
            <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${borders.default} ${surfaces.primary}`}>
              <SearchOutlined className={textColors.muted} />
              <input
                type="text"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search fonts..."
                aria-label="Search fonts"
                className={`min-w-0 flex-1 bg-transparent outline-none placeholder:text-gray-400 dark:placeholder:text-dark-400 ${typography.body} ${textColors.primary}`}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className={`${typography.caption} ${textColors.secondary} ${surfaces.hover} rounded-md px-2 py-1`}
                  aria-label="Clear font search"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        )}

        {filteredRecentChoices.length > 0 && (
          <div
            style={{ gridColumn: "1 / -1" }}
            className={`flex items-center gap-2 px-1 pt-1 ${typography.caption} ${textColors.secondary}`}
          >
            <span>{RECENT_FONTS_LABEL}</span>
            <div className={`flex-1 border-t ${borders.default}`} />
          </div>
        )}

        {filteredRecentChoices.map((choice) => (
          <FontChip
            key={`recent-${choice.value}`}
            value={choice.value}
            label={<span className="font-medium">{choice.label}</span>}
            previewText={previewText}
            selected={value === choice.value}
            onSelect={handleSelect}
          />
        ))}

        {filteredRecentChoices.length > 0 && filteredAllChoices.length > 0 && (
          <div
            style={{ gridColumn: "1 / -1" }}
            className={`flex items-center gap-2 px-1 pt-1 ${typography.caption} ${textColors.secondary}`}
          >
            <span>All Fonts</span>
            <div className={`flex-1 border-t ${borders.default}`} />
          </div>
        )}

        {filteredAllChoices.map((choice) => (
          <FontChip
            key={choice.value}
            value={choice.value}
            label={<span className="font-medium">{choice.label}</span>}
            previewText={previewText}
            selected={value === choice.value}
            onSelect={handleSelect}
          />
        ))}

        {isExpanded && !hasSearchResults && (
          <div
            style={{ gridColumn: "1 / -1" }}
            className={`rounded-lg border px-3 py-4 text-center ${borders.default} ${surfaces.primary} ${textColors.secondary} ${typography.body}`}
          >
            No fonts match your search.
          </div>
        )}
      </div>
      {help && <div ref={helpRef} className={`mt-1 ${typography.caption} ${textColors.tertiary}`}>{help}</div>}
    </div>
  );
}


